import { omit } from 'remeda';
import z from 'zod';

import { CURRENT_VERSION, PlateTextAttributes, UnityTextAttributes, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

export const UnityTextItem = z.object({
    text: z.string().nullable(),
    version: Version,
    type: z.literal(ELEMENT_TYPES.TEXT_ITEM),
    templateElement: z.boolean().optional(),
    attributes: UnityTextAttributes.optional(),
});
export type UnityTextItem = z.infer<typeof UnityTextItem>;

export const PlateTextItem = z
    .object({
        text: z.string(),
        ...PlateTextAttributes.shape,
    })
    .catchall(z.unknown());

// Type includes dynamic comment_<id> keys
export type PlateTextItem = z.infer<typeof PlateTextItem> &
    Record<`comment_${string}`, true | undefined>;

export const TextItemCodec = z.codec(UnityTextItem, PlateTextItem, {
    decode: (value) => {
        const attributes: Record<string, unknown> = { ...value.attributes };

        // Plate uses `underline` while Backend uses `underlined`
        if ('underlined' in attributes) {
            attributes.underline = attributes.underlined;
            delete attributes.underlined;
        }

        // Convert commentId to comment_<id> mark
        if (attributes.commentId) {
            attributes[`comment_${attributes.commentId}`] = true;
            attributes.comment = true;
            delete attributes.commentId;
        }

        return {
            text: value.text || '',
            ...attributes,
        };
    },
    encode: (value) => {
        const attributes: Record<string, unknown> = omit(value, ['text']);

        // Convert comment_<id> mark back to commentId (skip draft comments)
        const commentKey = Object.keys(attributes).find(
            (key) => key.startsWith('comment_') && key !== 'comment_draft',
        );
        if (commentKey) {
            attributes.commentId = commentKey.replace('comment_', '');
            delete attributes[commentKey];
            delete attributes.comment;
        }

        // Strip draft comment marks - they should never be saved
        delete attributes.comment_draft;
        if (!attributes.commentId) {
            delete attributes.comment;
        }

        // Plate uses `underline` while Backend uses `underlined`
        if ('underline' in attributes) {
            attributes.underlined = attributes.underline;
            delete attributes.underline;
        }

        const hasAttributes = Object.keys(attributes).length > 0;

        return {
            text: value.text,
            type: ELEMENT_TYPES.TEXT_ITEM,
            version: CURRENT_VERSION,
            ...(hasAttributes && { attributes }),
        };
    },
});
